import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    Badge,
    Card,
    Col,
    Container,
    Row,
} from 'react-bootstrap';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { NavLink } from 'react-router-dom';
import { FaEdit, FaEye } from 'react-icons/fa';

import SmartTopTool from '../../components/Smart/SmartTopTool';
import SmartPagination from '../../components/Smart/SmartPagination';
import SmartTable from '../../components/Smart/SmartTable';
import API from '../../api/axios';
import { useFormat } from '../../components/FormatContext';
import useCompanyContext from '../../hooks/useCompanyContext';

const SEARCH_TYPE = [
    {
        key: 'referenceNumber',
        label: 'Reservation Number',
    },
    {
        key: 'customerName',
        label: 'Customer name',
    },
    {
        key: 'customerLastName',
        label: 'Customer last name',
    },
    {
        key: 'customerEmail',
        label: 'Customer email',
    },
    {
        key: 'customerPhone',
        label: 'Customer phone',
    },
    {
        key: 'pickupShopName',
        label: 'Pickup shop',
    },
    {
        key: 'pickupCity',
        label: 'Pickup City',
    },
    {
        key: 'motorcycleModel',
        label: 'Motorcycle model',
    },
    {
        key: 'motorcycleBrand',
        label: 'Motorcycle brand',
    },
    {
        key: 'lastModifiedBy',
        label: 'Last modified by',
    },
];

const DATE_OPTIONS = [
    'createdAt',
    'startDate',
    'endDate',
];

const FILTER_OPTIONS = {
    status: [
        'UNPAID',
        'COMPLETED',
        'PENDING',
        'CONFIRMED',
        'INVOICED',
        'CANCELLED',
        'DISPUTED',
    ],
};

const DEFAULT_FILTERS = {
    dateFilters: [],
    status: [],
};

const formatPlainDate = (value) => {
    if (!value) {
        return '';
    }

    const dateString =
        typeof value === 'string'
            ? value
            : value.toISOString();

    return dateString.slice(0, 10);
};

const mapApiRow = (r) => {
    const pickupShop = r?.pickup?.shop ?? null;
    const pickupCity = r?.pickup?.location?.city ?? null;

    const dropoffShop = r?.dropoff?.shop ?? null;
    const dropoffCity = r?.dropoff?.location?.city ?? null;

    const model = r?.motorcycle?.model ?? null;

    const startDateStr = formatPlainDate(r.startDate);
    const endDateStr = formatPlainDate(r.endDate);

    const start =
        startDateStr && r.startTime
            ? `${startDateStr} ${r.startTime}`
            : startDateStr || r.startTime || null;

    const end =
        endDateStr && r.endTime
            ? `${endDateStr} ${r.endTime}`
            : endDateStr || r.endTime || null;

    return {
        id: r.id,
        reservationNumber: r.referenceNumber,
        date: formatPlainDate(r.createdAt),

        customer_name: r?.customer?.name ?? null,
        customer_lastname: r?.customer?.lastName ?? null,
        customer_email: r?.customer?.email ?? null,

        start,
        end,

        pickupShopId: pickupShop?.id ?? null,
        pickupShopName: pickupShop?.name ?? null,
        pickupShopCity: pickupCity ?? null,

        dropoffShopId: dropoffShop?.id ?? null,
        dropoffShopName: dropoffShop?.name ?? null,
        dropoffShopCity: dropoffCity ?? null,

        shopMotorcycleId: null,
        motorcycleName: model?.name ?? null,
        motorcycleType: model?.type ?? null,

        currency: r?.currency,
        finalPriceCents: r?.finalPriceCents ?? 0,

        status: r.status,
        lastModifiedBy: r.lastModifiedBy ?? null,

        groups: '',
        user_agent: '',
    };
};

const BookingsList = () => {
    const {
        baseFormatters,
        formatCurrency,
    } = useFormat();

    const {
        currentCompanyID,
        hasSpecificCompany,
        isAdmin,
        buildCompanyUrl,
    } = useCompanyContext();

    const [page, setPage] = useState({
        page_number: 1,
        total_pages: 1,
    });

    const [rows, setRows] = useState([]);

    const [meta, setMeta] = useState({
        total: 0,
        page: 1,
        pageSize: 10,
        totalPages: 1,
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [searchConditions, setSearchConditions] = useState([]);

    const [filters, setFilters] = useState(DEFAULT_FILTERS);

    const previousCompanyIdRef = useRef(currentCompanyID);
    const skipFetchRef = useRef(false);

    const fetchBookings = useCallback(async () => {
        try {
            setLoading(true);
            setError('');

            const params = new URLSearchParams();

            params.set(
                'page',
                String(page.page_number || 1),
            );

            params.set(
                'pageSize',
                String(meta.pageSize || 10),
            );

            searchConditions.forEach(
                ({
                    type,
                    value,
                }) => {
                    if (
                        value == null ||
                        String(value).trim() === ''
                    ) {
                        return;
                    }

                    const normalizedValue =
                        String(value).trim();

                    switch (type) {
                        case 'referenceNumber':
                        case 'customerName':
                        case 'customerLastName':
                        case 'customerEmail':
                        case 'customerPhone':
                        case 'pickupShopName':
                        case 'pickupCity':
                        case 'motorcycleModel':
                        case 'motorcycleBrand':
                        case 'lastModifiedBy':
                            params.set(
                                type,
                                normalizedValue,
                            );
                            break;

                        default:
                            break;
                    }
                },
            );

            filters.dateFilters.forEach(
                ({
                    type,
                    operator,
                    values = [],
                }) => {
                    if (!type) {
                        return;
                    }

                    const value1 = values[0];
                    const value2 = values[1];

                    switch (operator) {
                        case 'between':
                            if (value1) {
                                params.set(
                                    `${type}From`,
                                    value1,
                                );
                            }

                            if (value2) {
                                params.set(
                                    `${type}To`,
                                    value2,
                                );
                            }

                            break;

                        case 'before':
                            if (value1) {
                                params.set(
                                    `${type}To`,
                                    value1,
                                );
                            }

                            break;

                        case 'after':
                            if (value1) {
                                params.set(
                                    `${type}From`,
                                    value1,
                                );
                            }

                            break;

                        default:
                            break;
                    }
                },
            );

            if (filters.status.length > 0) {
                params.set(
                    'status',
                    String(filters.status[0]),
                );
            }

            if (hasSpecificCompany) {
                params.set(
                    'supplierId',
                    String(currentCompanyID),
                );
            }

            const queryString = params.toString();

            const url = queryString
                ? `/booking?${queryString}`
                : '/booking';

            const { data } = await API.get(url);

            const mapped = (
                data?.data || []
            ).map(mapApiRow);

            setRows(mapped);

            const total = data?.total ?? 0;
            const pageNum = data?.page ?? 1;
            const pageSize =
                data?.pageSize ??
                meta.pageSize;

            const totalPages = Math.max(
                1,
                Math.ceil(
                    total / pageSize,
                ),
            );

            setMeta({
                total,
                page: pageNum,
                pageSize,
                totalPages,
            });

            setPage((prev) => {
                if (
                    prev.page_number === pageNum &&
                    prev.total_pages === totalPages
                ) {
                    return prev;
                }

                return {
                    ...prev,
                    page_number: pageNum,
                    total_pages: totalPages,
                };
            });
        } catch (e) {
            setError(
                e?.response?.data?.message ||
                e?.message ||
                '加载失败',
            );
        } finally {
            setLoading(false);
        }
    }, [
        page.page_number,
        meta.pageSize,
        searchConditions,
        filters.dateFilters,
        filters.status,
        hasSpecificCompany,
        currentCompanyID,
    ]);

    const handleSetSearchConditions = useCallback(
        (valueOrUpdater) => {
            setSearchConditions((prev) =>
                typeof valueOrUpdater === 'function'
                    ? valueOrUpdater(prev)
                    : valueOrUpdater,
            );

            setPage((prev) => {
                if (prev.page_number === 1) {
                    return prev;
                }

                return {
                    ...prev,
                    page_number: 1,
                };
            });
        },
        [],
    );

    const handleSetFilters = useCallback(
        (valueOrUpdater) => {
            setFilters((prev) =>
                typeof valueOrUpdater === 'function'
                    ? valueOrUpdater(prev)
                    : valueOrUpdater,
            );

            setPage((prev) => {
                if (prev.page_number === 1) {
                    return prev;
                }

                return {
                    ...prev,
                    page_number: 1,
                };
            });
        },
        [],
    );

    const columns = useMemo(
        () => [
            {
                keys: [
                    'reservationNumber',
                    'date',
                ],
                label: 'Reservation Number',
                render: (item) => (
                    <div className="d-flex flex-column">
                        <span>
                            {item.reservationNumber}
                        </span>

                        <span className="fw-medium">
                            {item.date
                                ? baseFormatters.datetime.format(
                                    new Date(item.date),
                                )
                                : '-'}
                        </span>
                    </div>
                ),
            },
            {
                keys: [
                    'customer_name',
                    'customer_lastname',
                    'customer_email',
                ],
                label: 'Customer Information',
                render: (item) => (
                    <div className="d-flex flex-column">
                        <span>
                            {item.customer_name}{' '}
                            {item.customer_lastname}
                        </span>

                        <span>
                            {item.customer_email}
                        </span>
                    </div>
                ),
            },
            {
                keys: [
                    'start',
                    'end',
                ],
                label: 'Rental Date',
                render: (item) => (
                    <div className="d-flex flex-column">
                        <span>
                            {item.start ?? '-'}
                        </span>

                        <span>
                            {item.end ?? '-'}
                        </span>
                    </div>
                ),
            },
            {
                keys: [
                    'pickupShopId',
                    'pickupShopName',
                    'pickupShopCity',
                    'dropoffShopId',
                    'dropoffShopName',
                    'dropoffShopCity',
                    'shopMotorcycleId',
                    'motorcycleName',
                    'motorcycleType',
                ],
                label: 'Pickup & Dropoff',
                render: (item) => (
                    <div>
                        <div className="d-flex align-items-center mb-2">
                            <Badge
                                bg="primary"
                                className="me-2"
                                style={{
                                    minWidth: '100px',
                                    fontSize: '14px',
                                }}
                            >
                                {item.pickupShopCity || '-'}
                            </Badge>

                            <OverlayTrigger
                                placement="top"
                                overlay={
                                    <Tooltip
                                        id={`tooltip-${item.pickupShopId}`}
                                    >
                                        Pick up Shop ID:{' '}
                                        {item.pickupShopId}
                                    </Tooltip>
                                }
                            >
                                <span
                                    style={{
                                        cursor: 'pointer',
                                    }}
                                >
                                    {item.pickupShopName || '-'}
                                </span>
                            </OverlayTrigger>
                        </div>

                        {item.pickupShopId &&
                            item.dropoffShopId &&
                            item.pickupShopId !==
                            item.dropoffShopId && (
                                <div className="d-flex align-items-center mb-2">
                                    <Badge
                                        bg="primary"
                                        className="me-2"
                                        style={{
                                            minWidth: '100px',
                                            fontSize: '14px',
                                        }}
                                    >
                                        {item.dropoffShopCity || '-'}
                                    </Badge>

                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            <Tooltip
                                                id={`tooltip-${item.dropoffShopId}`}
                                            >
                                                Drop off Shop ID:{' '}
                                                {item.dropoffShopId}
                                            </Tooltip>
                                        }
                                    >
                                        <span
                                            style={{
                                                cursor: 'pointer',
                                            }}
                                        >
                                            {item.dropoffShopName || '-'}
                                        </span>
                                    </OverlayTrigger>
                                </div>
                            )}

                        <div className="d-flex align-items-center mb-2">
                            <Badge
                                bg="primary"
                                className="me-2"
                                style={{
                                    minWidth: '100px',
                                    fontSize: '14px',
                                }}
                            >
                                {item.motorcycleType || '-'}
                            </Badge>

                            <OverlayTrigger
                                placement="top"
                                overlay={
                                    <Tooltip
                                        id={`tooltip-moto-${item.id}`}
                                    >
                                        Motorcycle
                                    </Tooltip>
                                }
                            >
                                <span
                                    style={{
                                        cursor: 'pointer',
                                    }}
                                >
                                    {item.motorcycleName || '-'}
                                </span>
                            </OverlayTrigger>
                        </div>
                    </div>
                ),
            },
            {
                key: 'finalPriceCents',
                label: 'Total Price',
                render: (item) => {
                    const currency =
                        item.currency || 'EUR';

                    const amount =
                        Number(
                            item.finalPriceCents || 0,
                        ) / 100;

                    return (
                        <span className="fw-semibold">
                            {formatCurrency(
                                amount,
                                currency,
                            )}
                        </span>
                    );
                },
            },
            {
                keys: ['status'],
                label: 'Status',
                render: (item) => {
                    const statusColorMap = {
                        Accepted: {
                            bg: 'bgColor-accepted',
                            text: 'text-success-700',
                        },
                        Cancelled: {
                            bg: 'bgColor-cancelled',
                            text: 'text-danger-700',
                        },
                        Pending: {
                            bg: 'bgColor-pending',
                            text: 'text-warning-700',
                        },
                        Unpaid: {
                            bg: 'bgColor-unpaid',
                            text: 'text-gray-700',
                        },
                        Completed: {
                            bg: 'bgColor-completed',
                            text: 'text-primary-700',
                        },
                        Invoiced: {
                            bg: 'bgColor-invoiced',
                            text: 'text-purple-700',
                        },
                        Disputed: {
                            bg: 'bgColor-disputed',
                            text: 'text-amber-800',
                        },
                    };

                    const colorClass =
                        statusColorMap[item.status] || {};

                    return (
                        <div className="d-flex flex-column">
                            <div
                                className={`
                                    d-inline-flex
                                    align-items-center
                                    justify-content-center
                                    rounded
                                    px-2
                                    py-1
                                    border
                                    border-1
                                    ${colorClass.bg || ''}
                                    ${colorClass.text || ''}
                                `}
                            >
                                <span className="small fs-6">
                                    {item.status || '-'}
                                </span>
                            </div>

                            {item.lastModifiedBy ? (
                                <div className="small text-muted mt-2">
                                    Last modified by:{' '}
                                    {item.lastModifiedBy}
                                </div>
                            ) : null}
                        </div>
                    );
                },
            },
            {
                key: 'actions',
                label: 'Actions',
                render: (item) => (
                    <div className="d-flex gap-2 align-items-center">
                        <NavLink
                            to={buildCompanyUrl(
                                `/bookings/${item.id}/supplier-decision`,
                            )}
                            className="btn btn-edit p-0"
                            title="view"
                        >
                            <FaEye
                                size={20}
                                style={{
                                    marginLeft: '4px',
                                }}
                            />
                        </NavLink>

                        {isAdmin && (
                            <NavLink
                                to={buildCompanyUrl(
                                    `/bookings/edit/${item.id}`,
                                )}
                                className="btn btn-edit p-0"
                                title="Edit"
                            >
                                <FaEdit
                                    size={20}
                                    style={{
                                        marginLeft: '4px',
                                    }}
                                />
                            </NavLink>
                        )}
                    </div>
                ),
            },
        ],
        [
            baseFormatters,
            formatCurrency,
            isAdmin,
            buildCompanyUrl,
        ],
    );

    /*
     * Company 变化：
     *
     * - 清空搜索
     * - 清空 filters
     * - 回到第一页
     *
     * 如果本身已经没有条件并且已经在第一页，
     * 不需要额外 reset，fetchBookings 会因为
     * currentCompanyID 变化自动重新请求。
     */
    useEffect(() => {
        if (
            previousCompanyIdRef.current ===
            currentCompanyID
        ) {
            return;
        }

        previousCompanyIdRef.current =
            currentCompanyID;

        const needsReset =
            searchConditions.length > 0 ||
            filters.dateFilters.length > 0 ||
            filters.status.length > 0 ||
            page.page_number !== 1;

        if (!needsReset) {
            return;
        }

        /*
         * currentCompanyID 改变本身会先让 fetchBookings
         * identity 变化。
         *
         * 由于这里还需要 reset conditions/page，
         * 跳过这一轮旧条件请求，等 reset 完成后再请求。
         */
        skipFetchRef.current = true;

        setSearchConditions([]);
        setFilters(DEFAULT_FILTERS);

        setPage((prev) => {
            if (prev.page_number === 1) {
                return prev;
            }

            return {
                ...prev,
                page_number: 1,
            };
        });
    }, [
        currentCompanyID,
        searchConditions.length,
        filters.dateFilters.length,
        filters.status.length,
        page.page_number,
    ]);

    /*
     * 唯一负责列表请求的 effect。
     *
     * fetchBookings 的任何真实依赖变化，
     * 都会自动触发重新请求。
     */
    useEffect(() => {
        if (skipFetchRef.current) {
            skipFetchRef.current = false;
            return;
        }

        fetchBookings();
    }, [fetchBookings]);

    return (
        <Container
            fluid
            className="d-flex flex-column"
        >
            <Row className="mb-4">
                <Col sm={8}>
                    <span className="fw-semibold">
                        Bookings List
                    </span>
                </Col>

                <Col
                    sm={4}
                    className="text-end"
                >
                    <span className="fw-semibold">
                        <NavLink
                            to={buildCompanyUrl(
                                '/dashboard',
                            )}
                            className="block-link"
                        >
                            Dashboard
                        </NavLink>{' '}
                        - Bookings
                    </span>
                </Col>
            </Row>

            <Row>
                <Col sm={12}>
                    <Card className="h-100 p-2">
                        <Card.Body className="px-2 pt-2 pb-5">
                            <Row>
                                <Col md={10}>
                                    <SmartTopTool
                                        searchType={
                                            SEARCH_TYPE
                                        }
                                        searchConditions={
                                            searchConditions
                                        }
                                        setSearchConditions={
                                            handleSetSearchConditions
                                        }
                                        dateOptions={
                                            DATE_OPTIONS
                                        }
                                        filterOptions={
                                            FILTER_OPTIONS
                                        }
                                        filters={
                                            filters
                                        }
                                        setFilters={
                                            handleSetFilters
                                        }
                                    />
                                </Col>

                                {isAdmin && (
                                    <Col
                                        md={2}
                                        className="d-flex justify-content-end"
                                    >
                                        <NavLink
                                            to={buildCompanyUrl(
                                                '/bookings/new',
                                            )}
                                            className="border ms-1 btn-custom-success btn btn-primary"
                                            title="New"
                                            style={{
                                                height: '40px',
                                            }}
                                        >
                                            + New Booking
                                        </NavLink>
                                    </Col>
                                )}

                                <Col md={12}>
                                    <SmartTable
                                        data={rows}
                                        columns={columns}
                                        loading={loading}
                                        emptyState={
                                            <div className="text-center py-5 text-muted">
                                                <p>
                                                    {error ||
                                                        'No matching bookings found'}
                                                </p>
                                            </div>
                                        }
                                    />
                                </Col>

                                <Col
                                    md={12}
                                    className="d-flex justify-content-end"
                                >
                                    <SmartPagination
                                        page={page}
                                        setPage={setPage}
                                    />
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default BookingsList;